import { NavLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/contexts/I18nContext";
import { COMPOSER_URL } from "@/lib/links";
import type { UserRole } from "@/types";

/**
 * The left-hand navigation.
 *
 * Items are **hidden** when the role lacks access, never disabled. A greyed-out
 * "Billing" tells a teacher that something exists which they may not have, and
 * invites them to ask why; an absent one tells them nothing, which is correct —
 * billing is not their job.
 *
 * Rows are 36px with a `brand-soft` pill on the current one — the only place
 * in the rail that carries colour, so a glance answers "where am I". The rail
 * itself is white, like the page: the item you are on is the difference.
 */

interface NavItem {
  to: string;
  labelKey: string;
  roles: readonly UserRole[];
  /** An item that leaves this app — Composer is its own front door. */
  external?: boolean;
}

const STAFF: readonly UserRole[] = ["owner", "teacher"];
const OWNER: readonly UserRole[] = ["owner"];

export const NAV_ITEMS: readonly NavItem[] = [
  { to: "/", labelKey: "nav.dashboard", roles: STAFF },
  { to: "/people", labelKey: "nav.people", roles: OWNER },
  { to: "/groups", labelKey: "nav.groups", roles: STAFF },
  { to: "/devices", labelKey: "nav.devices", roles: OWNER },
  // Staff, not owners only: a teacher setting up a room needs the network.
  { to: "/kits", labelKey: "nav.kits", roles: STAFF },
  { to: COMPOSER_URL, labelKey: "nav.composer", roles: STAFF, external: true },
  { to: "/sessions", labelKey: "nav.sessions", roles: STAFF },
  { to: "/reports", labelKey: "nav.reports", roles: STAFF },
  { to: "/institution", labelKey: "nav.institution", roles: OWNER },
  { to: "/consent", labelKey: "nav.consent", roles: STAFF },
  { to: "/legal", labelKey: "nav.legal", roles: STAFF },
  { to: "/support", labelKey: "nav.support", roles: OWNER },
  { to: "/billing", labelKey: "nav.billing", roles: OWNER },
];

/** Exported for the guard test: the same list decides both. */
export function visibleNavItems(role: UserRole | undefined): readonly NavItem[] {
  if (!role) return [];
  return NAV_ITEMS.filter((item) => item.roles.includes(role));
}

const linkClass = (active: boolean) =>
  `flex h-9 items-center rounded-control px-3 text-[14px] transition-colors ${
    active
      ? "bg-brand-soft font-medium text-brand-ink"
      : "text-ink-soft hover:bg-surface-muted hover:text-ink"
  }`;

export function Nav() {
  const t = useT();
  const { user } = useAuth();
  const items = visibleNavItems(user?.role);

  return (
    <nav aria-label={t("nav.menu")} className="flex flex-col gap-0.5 p-4">
      {items.map((item) =>
        item.external ? (
          <a
            key={item.to}
            href={item.to}
            className={linkClass(false)}
          >
            {t(item.labelKey)}
            {/* The arrow says "you are leaving Backstage" — same sign-in, new door. */}
            <span aria-hidden="true" className="ml-1 text-ink-faint">↗</span>
          </a>
        ) : (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            className={({ isActive }) => linkClass(isActive)}
          >
            {t(item.labelKey)}
          </NavLink>
        )
      )}
    </nav>
  );
}
